import type { Filter, FiltersGroupId } from '@/app/common/filters/core/filtersTypes.ts';
import { CUSTOM_GROUP_NAME, FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { sortedStarsNumbers, starsWrappedText } from '@/app/common/filters/core/starsLabel.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import { EVO_RATING_4_PLUS } from '@/app/common/filters/core/ratingValues.ts';
import { FACILITY_GROUP_FILTER_NAMES } from '@/app/common/filters/groups/facilityGroups.ts';

export enum QUICK_FILTER {
  STARS_4_TO_5 = 'stars4to5',
  RATING_4_PLUS = 'rating4plus',
  BREAKFAST = 'breakfast',
  FULL_BOARD = 'fullBoard',
  ALL_INCLUSIVE = 'allInclusive',
  HIGH_SPEED_INTERNET = 'highSpeedInternet',
  PARKING = 'parking',
  SWIMMING_POOL = 'swimmingPool',
  SPA = 'spa',
  ACCESSIBILITY = 'accessibility',
  KIDS_CLUB = 'kidsClub',
  RESTAURANT_BAR = 'restaurantBar',
  AIR_CONDITIONING = 'airConditioning',
}

export type QuickFilterTarget = {
  filterName: FILTER_NAME;
  values: string[];
};

export type QuickFilter = {
  id: string;
  groupId: FiltersGroupId;
  targets: QuickFilterTarget[];
  translationKey?: string;
  translationValues?: Record<string, string>;
  // Wins over translationKey, for wording assembled from more than one key.
  buildLabel?: (t: TranslationFunction, values: string[]) => string;
};

const target = (filterName: FILTER_NAME, ...values: string[]): QuickFilterTarget => ({ filterName, values });

const single = (groupId: FiltersGroupId, filterName: FILTER_NAME, ...values: string[]): Omit<QuickFilter, 'id'> => ({
  groupId,
  targets: [target(filterName, ...values)],
});

const facility = (
  legacy: QuickFilterTarget,
  api: QuickFilterTarget,
  translationKey: string
): Omit<QuickFilter, 'id'> => ({
  groupId: CUSTOM_GROUP_NAME.FACILITIES,
  targets: [legacy, api],
  translationKey,
});

// The old facilities lack it, so it shows only under the toggle, in whichever group the api puts it.
const apiFacility = (code: string): Omit<QuickFilter, 'id'> => ({
  groupId: CUSTOM_GROUP_NAME.FACILITIES,
  targets: FACILITY_GROUP_FILTER_NAMES.map((name) => target(name, code)),
});

const specs: Record<QUICK_FILTER, Omit<QuickFilter, 'id'>> = {
  [QUICK_FILTER.STARS_4_TO_5]: {
    ...single(FILTER_NAME.STARS, FILTER_NAME.STARS, '4', '5'),
    // Same keys as the pill, so the chip cannot drift from it.
    translationKey: 'vacation.oss.filters.stars.placeholder',
    buildLabel: (t, values) => starsWrappedText(sortedStarsNumbers(values), t).text,
  },
  [QUICK_FILTER.RATING_4_PLUS]: {
    // Only the evo list offers this bucket, so resolution drops the chip on the TSX filters.
    ...single(FILTER_NAME.RATING, FILTER_NAME.RATING, EVO_RATING_4_PLUS.value),
    // Same key and number as the rating pill, so the chip cannot drift from it.
    translationKey: 'vacation.oss.filters.rating.selected_rating',
    translationValues: { number: EVO_RATING_4_PLUS.score.toFixed(1) },
  },
  [QUICK_FILTER.BREAKFAST]: single(FILTER_NAME.MEAL_PLAN, FILTER_NAME.MEAL_PLAN, '2'),
  [QUICK_FILTER.FULL_BOARD]: single(FILTER_NAME.MEAL_PLAN, FILTER_NAME.MEAL_PLAN, '4'),
  [QUICK_FILTER.ALL_INCLUSIVE]: single(FILTER_NAME.MEAL_PLAN, FILTER_NAME.MEAL_PLAN, '5'),
  [QUICK_FILTER.HIGH_SPEED_INTERNET]: facility(
    target(FILTER_NAME.FACILITIES_GENERAL, '0'),
    target(FILTER_NAME.FACILITIES_INTERNET, 'ht03_wifi'),
    'vacation.search.filters.facilities.options.freewifi'
  ),
  [QUICK_FILTER.PARKING]: facility(
    target(FILTER_NAME.FACILITIES_GENERAL, '3'),
    target(FILTER_NAME.FACILITIES_PARKING, 'ht03_park'),
    'vacation.search.filters.facilities.options.parking'
  ),
  [QUICK_FILTER.SWIMMING_POOL]: facility(
    target(FILTER_NAME.FACILITIES_GENERAL, '4'),
    target(FILTER_NAME.FACILITIES_POOL_WELLNESS, 'ht03_swim'),
    'vacation.search.filters.facilities.options.swimmingpool'
  ),
  [QUICK_FILTER.SPA]: facility(
    target(FILTER_NAME.FACILITIES_GENERAL, '6'),
    target(FILTER_NAME.FACILITIES_ACTIVITIES, 'ht03_spa'),
    'vacation.search.filters.facilities.options.spa'
  ),
  [QUICK_FILTER.ACCESSIBILITY]: facility(
    target(FILTER_NAME.FACILITIES_GENERAL, '2'),
    target(FILTER_NAME.FACILITIES_ACCESSIBILITY, 'ht03_wheel'),
    'vacation.search.filters.facilities.options.disableguest'
  ),
  [QUICK_FILTER.KIDS_CLUB]: facility(
    target(FILTER_NAME.FACILITIES_FAMILY, '17'),
    target(FILTER_NAME.FACILITIES_ACTIVITIES, 'ht03_kids'),
    'vacation.oss.filters.family.club'
  ),
  [QUICK_FILTER.RESTAURANT_BAR]: facility(
    target(FILTER_NAME.FACILITIES_FOOD, '58'),
    target(FILTER_NAME.FACILITIES_FOOD_DRINK, 'ht03_rest'),
    'vacation.oss.filters.food.restaurant'
  ),
  [QUICK_FILTER.AIR_CONDITIONING]: apiFacility('ht03_air'),
};

export const QUICK_FILTER_CATALOGUE = Object.fromEntries(
  Object.entries(specs).map(([id, spec]) => [id, { id, ...spec }])
) as Record<QUICK_FILTER, QuickFilter>;

export const quickFilterById = (id: string): QuickFilter | undefined =>
  Object.hasOwn(QUICK_FILTER_CATALOGUE, id) ? QUICK_FILTER_CATALOGUE[id as QUICK_FILTER] : undefined;

export const quickFilterTargetOf = (quickFilter: QuickFilter, filter: Filter): QuickFilterTarget | undefined =>
  quickFilter.targets.find((candidate) => candidate.filterName === filter.name);
